import { Component, EventEmitter, Injector, Output } from '@angular/core';
import { stepperEnum } from '../../../common/enum/bulkExportEnum';
import { Art57Section } from '../../../ui/Art57Section/Art57Section';
import { SectionService } from '../../../service/section-service/section-service';
import { PageChangeType } from '../../../common/enum/PageChangeTypeEnum';
import { LoggerService } from '../../../service/logger-service/logger.service';
import { environment } from '../../../../../environments/environment';
import { BulkSection } from '../../../ui/Art57Section/BulkSection';
import { BulkService } from '../../../service/bulk-service/bulk.service';
import { BulkOperationSourceEnum } from '../../../ui/Node/MedicinalProductsNode/BulkOperationSourceEnum';

@Component({
  selector: 'app-bulk-export-toolbox',
  templateUrl: './bulk-export-toolbox.component.html',
  styleUrl: './bulk-export-toolbox.component.scss'
})
export class BulkExportToolboxComponent {
  @Output() buttonAction = new EventEmitter();
  disableCount: boolean = false;
  constructor (private injector: Injector) {}

  onButtonAction(buttonAction: String, parameter: string = '') {
    switch (buttonAction) {
      case 'count':
        this.count();
        break;
      case 'request':
        this.request();
        break;
      case 'refresh':
        this.refresh();
        break;
      case 'firstPage':
        this.changeRequestsPage(PageChangeType.First);
        break;
      case 'previousPage':
        this.changeRequestsPage(PageChangeType.Previous);
        break;
      case 'nextPage':
        this.changeRequestsPage(PageChangeType.Next);
        break;
      case 'cancelRequest':
        this.changeRequestsPage(PageChangeType.Cancel);
        break;
      case 'downloadRequest':
        this.changeRequestsPage(PageChangeType.Download);
        break;
      case 'archiveRequest':
        this.changeRequestsPage(PageChangeType.Archive);
        break;
      default:
        const data = { action: buttonAction, param: parameter };
        this.buttonAction.emit(data);
    }
  };

  count() {
    this.injector.get(Art57Section).showSuccessTooltipMessage('Requesting Counting','',environment.successElapsedTime);
    this.disableCount = true;
    this.injector.get(BulkService).count(this.injector.get(Art57Section).bulkexport.criteriaModel).subscribe({
          next: (data) => {
            this.injector.get(Art57Section).bulkexport.criteriaModel.filterCount = data;
            this.injector.get(Art57Section).showSuccessTooltipMessage('Counting Completed','',environment.successElapsedTime);
            this.injector.get(SectionService).notifyUpdatedBulkModel(BulkOperationSourceEnum.EXPORT);
            const countValue = this.getCountValue();
            if ( countValue==undefined || countValue == '(0)') {
              this.injector.get(Art57Section).showWarningTooltipMessage('No results found, please review filters section','',environment.successElapsedTime);
            }
            this.disableCount = false;
           },
           error:() => {
            this.disableCount = false;
           }
          }
        );      
  }

  getCountLabel(){
    if (!this.disableCount) {
      return 'Count ' + this.getCountValue();   
    }

    return 'Counting';   
  }

  getCountValue():string {
   const countValue: String = this.injector.get(Art57Section).bulkexport.criteriaModel.filterCount;
   return countValue != null || countValue != undefined ? `(${countValue})`:'';
  }
  
  request() {
    const bulkSection = this.injector.get(Art57Section).bulkexport as BulkSection;
    const bulkService = this.injector.get(BulkService);
    const sectionService = this.injector.get(SectionService);

    this.injector.get(Art57Section).showSuccessTooltipMessage('Requesting Export');
    bulkSection.selectedStep = stepperEnum.secondStep;
    sectionService.notifyUpdatedBulkModel(BulkOperationSourceEnum.EXPORT);
    this.injector.get(Art57Section).showSuccessTooltipMessage('Waiting to be Queued');

    bulkService.request(bulkSection.criteriaModel).subscribe({
      next: (data) => {
        const count = data;
        this.injector.get(Art57Section).bulkexport.criteriaModel.filterCount = count;
        if (count > 0) {
          bulkService.notifyRefresh(BulkOperationSourceEnum.EXPORT);
          this.injector.get(Art57Section).showSuccessTooltipMessage('Export successfully requested');
        } else {
          this.injector.get(Art57Section).showWarningTooltipMessage('Request not submitted as the result set is empty');
        }
      },
      error: (err) => {
        this.injector.get(Art57Section).showErrorTooltipMessage('Requesting Export failed');
        this.injector.get(LoggerService).err(err);
      }
    });
  }

  refresh() {
    this.injector.get(BulkService).notifyRefresh(BulkOperationSourceEnum.EXPORT);
  }

  isFirstStep(){
    return this.injector.get(Art57Section).bulkexport.isStepperFirstStep();
  }

  isSecondStep(){
    return this.injector.get(Art57Section).bulkexport.isStepperSecondStep();
  }

  changeRequestsPage(pageChangeType: PageChangeType): void {
    const bulkExportService = this.injector.get(BulkService);
    bulkExportService.notifyPageChange(pageChangeType,BulkOperationSourceEnum.EXPORT);
  }

  // Label for Paginator for the requests
  getCurrentPageLabel(): string {
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const pagination = section?.cachedRequests?.paginationControl;

    if (!pagination || pagination.currentPage == null || isNaN(pagination.currentPage)) {
      return '1'; // fallback (page index 0 → page 1)
    }

    const currentIndex = pagination.currentPage; // 0‑based
    return (currentIndex + 1).toString();
  }

  // True - you are in the first page in the Paginator; otherwise false
  isFirstPage(): boolean {
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const pagination = section?.cachedRequests?.paginationControl;

    if (!pagination || pagination.currentPage == null || isNaN(pagination.currentPage)) {
      // Treat "unknown" as first page to stay safe
      return true;
    }

    return pagination.isFirstPage();
  }

  isCancelable(): boolean {
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const selectedItem = section?.selectedItem;
    return selectedItem && selectedItem.isCancelable;
  }

  isDownloadable():boolean {
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const selectedItem = section?.selectedItem;
    return selectedItem && selectedItem.isDownloadable;
  }

  isArchivable():boolean{
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const selectedItem = section?.selectedItem;
    return selectedItem && selectedItem.isArchivable;
  }

  hasActions():boolean{
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const selectedItem = section?.selectedItem;
    return selectedItem && selectedItem.hasActions;
  }

  // True - if there is next page; False there is not next page, you are already in the next page
  hasNextPage(): boolean {
    const section = this.injector.get(Art57Section).bulkexport as BulkSection;
    const cache = section?.cachedRequests;

    // If undefined, be conservative and assume there might be a next page.
    return cache?.hasNextPage ?? true;
  }

}